import { motion } from "framer-motion";
import { Users2, Send, CalendarClock, Wallet } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionTag } from "./SectionTag";

const points = [
  {
    icon: Wallet,
    title: "Zéro app, zéro coût par envoi",
    description: "Le message arrive dans le wallet déjà installé sur leur téléphone — pas de SMS à payer, pas d'app à faire télécharger.",
  },
  {
    icon: Users2,
    title: "Ciblez qui vous voulez",
    description: "Les clients inactifs depuis 30 jours, vos plus gros dépensiers, ou tout le monde en un clic.",
  },
  {
    icon: CalendarClock,
    title: "Maintenant ou plus tard",
    description: "Envoyez à l'instant pour un coup de chaud, ou planifiez une campagne récurrente qui tourne toute seule.",
  },
];

export function PushNotificationFeature() {
  return (
    <section className="bg-cream-deep py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <SectionTag>Notifications push</SectionTag>
          <h2 className="mt-5 text-4xl font-extrabold tracking-tight text-ink lg:text-5xl">
            Un message, et vos clients
            <br />
            <span className="text-gradient">reviennent le jour même.</span>
          </h2>
          <p className="mt-4 text-lg text-ink-soft">
            Directement sur l'écran verrouillé de leur téléphone — sans app, sans SMS à payer,
            sans effort de leur part pour le voir.
          </p>
        </Reveal>

        <div className="mt-16 grid items-center gap-12 lg:grid-cols-2">
          <Reveal delay={0.1} className="order-2 lg:order-1">
            <div className="flex flex-col gap-6">
              {points.map((point) => (
                <div key={point.title} className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-brand text-white">
                    <point.icon size={20} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-ink">{point.title}</h3>
                    <p className="mt-1 text-sm text-ink-soft">{point.description}</p>
                  </div>
                </div>
              ))}
              <div className="mt-2 flex items-center gap-2 rounded-2xl border border-line bg-white px-4 py-3 text-sm text-ink-soft">
                <Send size={15} className="shrink-0 text-coral" />
                Depuis votre tableau de bord : tapez votre message, choisissez les destinataires,
                c&rsquo;est envoyé.
              </div>
            </div>
          </Reveal>

          <Reveal className="order-1 lg:order-2">
            <div className="relative mx-auto w-[280px] rounded-[2.75rem] border-[10px] border-ink bg-gradient-to-b from-[#2a2a45] to-[#14142b] p-1.5 shadow-2xl">
              <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-ink" />
              <div className="flex min-h-[560px] flex-col overflow-hidden rounded-[2rem] px-4 pb-6 pt-4 text-white">
                <div className="flex items-center justify-between px-1 text-[11px] font-semibold">
                  <span>13:20</span>
                  <span className="flex gap-1 opacity-70">📶 🔋</span>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-5xl font-extralight tracking-tight">13:20</p>
                  <p className="mt-1 text-sm text-white/60">Jeudi 22 août</p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: -12, scale: 0.96 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="mt-8 rounded-2xl bg-white/95 p-3.5 shadow-xl backdrop-blur"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-brand text-white">
                      <Wallet size={15} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-bold text-ink">Boulangerie Le Fournil</p>
                        <span className="shrink-0 text-[10px] text-ink-soft">maintenant</span>
                      </div>
                      <p className="mt-0.5 text-xs leading-snug text-ink-soft">
                        Le café offert vous attend depuis 2 semaines ☕ Passez nous voir avant qu&rsquo;il
                        n&rsquo;expire !
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="mt-3 rounded-2xl bg-white/10 p-3.5"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15 text-white">
                      <Wallet size={15} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-bold text-white/80">Salon Belle Coiffure</p>
                        <span className="shrink-0 text-[10px] text-white/60">il y a 2h</span>
                      </div>
                      <p className="mt-0.5 text-xs leading-snug text-white/50">
                        -20% sur votre prochaine couleur, ce week-end seulement.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
